// Can this package actually be booked at this time?
//
// A package takes every court the admin assigned to it, for a run of whole
// hours, plus a cleanup gap afterwards. Three separate things can stop it,
// and a guest deserves to be told which:
//
//   1. It doesn't fit the package's own window   ("finishes after we close")
//   2. The club has no slot configured for an hour it needs
//   3. Someone already holds one of those court-hours — an hourly booking,
//      another package, or that package's cleanup gap
//
// Everything here is pure: it takes the `bookedSlots` map App.jsx already
// subscribes to for the viewed date ({ [timeSlot]: [{ court, ... }] }) and
// returns answers, so no extra Firebase round trip is needed to grey out an
// impossible start time. The authoritative check is still the slot-lock
// transaction at booking time (claimBookingSlots) — this is the fast,
// friendly layer in front of it.

import { getCourtLabel, getWeekdayFromDateStr } from './bookingUtils';
import {
  getPackageCourts,
  getPackageStartHours,
  getPackageOccupiedHours,
  getSlotLabelForHour,
  getPackageWindowHours,
  formatPackageHour,
  describePackageWindow,
  getPackageBufferMinutes,
} from './packageUtils';

export const PACKAGE_CONFLICT = {
  PAST: 'past',
  OUTSIDE_WINDOW: 'outside-window',
  NO_SLOT: 'no-slot',
  COURT_TAKEN: 'court-taken',
  NO_COURTS: 'no-courts',
};

/**
 * Which courts (if any) are already occupied for a given slot label.
 * @param {object} bookedSlots - { [timeSlot]: [{ court, userName, status }] }
 */
const takenCourtsAt = (bookedSlots, slotLabel) =>
  ((bookedSlots || {})[slotLabel] || []).map(entry => Number(entry.court));

/**
 * Whether a package can start at `startHour` for `hours`, and why not.
 *
 * @param {object} params
 * @param {object} params.config
 * @param {object} params.pkg
 * @param {number} params.startHour - absolute; may be >= 24 for a window that wraps
 * @param {number} params.hours
 * @param {object} params.bookedSlots - for the event's date
 * @param {number|null} [params.currentHour] - pass for "today" to rule out past starts
 * @returns {{ ok: boolean, code?: string, message?: string }}
 */
export const checkPackageStart = ({ config, pkg, startHour, hours, bookedSlots, currentHour = null }) => {
  const duration = Number(hours) || 0;
  const start = Number(startHour) || 0;
  const courts = getPackageCourts(pkg);

  if (courts.length === 0) {
    return {
      ok: false,
      code: PACKAGE_CONFLICT.NO_COURTS,
      message: 'This package has no courts assigned yet. Please contact us so we can set it up for you.',
    };
  }

  // 1. Inside the package's advertised window? This is the guest-facing half
  //    of the spec's first scenario: an 11PM start on a 4-hour package when
  //    the offer only runs to midnight.
  if (!getPackageStartHours(pkg, duration).includes(start)) {
    const windowHours = getPackageWindowHours(pkg);
    return {
      ok: false,
      code: PACKAGE_CONFLICT.OUTSIDE_WINDOW,
      message: duration > windowHours
        ? `A ${duration}-hour booking doesn't fit — this package runs ${describePackageWindow(pkg)}.`
        : `Starting at ${formatPackageHour(start)} would finish after this package closes. `
          + `It runs ${describePackageWindow(pkg)}, so a ${duration}-hour booking can start no later than `
          + `${formatPackageHour(getPackageStartHours(pkg, duration).slice(-1)[0])}.`,
    };
  }

  // 2. Today only: an hour that has already begun can't be sold.
  if (currentHour !== null && start <= currentHour) {
    return {
      ok: false,
      code: PACKAGE_CONFLICT.PAST,
      message: `${formatPackageHour(start)} has already passed today. Please pick a later start time or another date.`,
    };
  }

  const { eventHours, bufferHours } = getPackageOccupiedHours(config, pkg, start, duration);

  // 3. Every hour needs a configured slot to lock against — see
  //    getSlotLabelForHour. A cleanup hour with no slot is NOT fatal: there's
  //    simply nothing to hold, which is the same outcome as a zero allowance.
  const missing = eventHours.filter(h => !getSlotLabelForHour(config, h));
  if (missing.length > 0) {
    return {
      ok: false,
      code: PACKAGE_CONFLICT.NO_SLOT,
      message: `We're not open for the full booking at that time — ${formatPackageHour(missing[0])} isn't part of our opening hours.`,
    };
  }

  // 4. Already taken. Reported per hour+court so the message names the actual
  //    clash rather than a bare "unavailable" — the spec's second scenario.
  const clashes = [];
  [...eventHours, ...bufferHours].forEach((hour) => {
    const label = getSlotLabelForHour(config, hour);
    if (!label) return;
    const taken = takenCourtsAt(bookedSlots, label);
    courts.forEach((court) => {
      if (taken.includes(court)) clashes.push({ hour, court, label, isBuffer: bufferHours.includes(hour) });
    });
  });

  if (clashes.length > 0) {
    const first = clashes[0];
    const courtNames = [...new Set(clashes.map(c => getCourtLabel(config, c.court)))].join(', ');
    const from = formatPackageHour(clashes[0].hour);
    const to = formatPackageHour(clashes[clashes.length - 1].hour + 1);
    return {
      ok: false,
      code: PACKAGE_CONFLICT.COURT_TAKEN,
      message: first.isBuffer && clashes.every(c => c.isBuffer)
        // The event itself fits; only the cleanup gap runs into something.
        ? `That start time works, but we need ${getPackageBufferMinutes(config, pkg)} minutes after your event to reset the courts, `
          + `and ${courtNames} ${clashes.length > 1 ? 'are' : 'is'} already booked from ${from}. Please pick an earlier start time.`
        : `${courtNames} ${clashes.length > 1 ? 'are' : 'is'} already booked between ${from} and ${to}. `
          + 'Please choose a different start time or another date.',
      clashes,
    };
  }

  return { ok: true };
};

/**
 * Every start hour a package of `hours` can actually be booked at on a date,
 * each flagged with whether it's selectable and why not.
 *
 * Returned as a full list rather than only the free ones so the picker can
 * show unavailable times greyed out with a reason, which tells a guest more
 * than a silently shorter dropdown.
 *
 * @returns {Array<{ hour: number, label: string, available: boolean, reason: string|null, code: string|null }>}
 */
export const getPackageStartOptions = ({ config, pkg, hours, bookedSlots, currentHour = null }) =>
  getPackageStartHours(pkg, hours).map((hour) => {
    const check = checkPackageStart({ config, pkg, startHour: hour, hours, bookedSlots, currentHour });
    return {
      hour,
      label: formatPackageHour(hour),
      available: check.ok,
      reason: check.ok ? null : check.message,
      code: check.ok ? null : check.code,
    };
  });

/**
 * Every END hour a booking starting at `startHour` could run to, each flagged
 * with whether it's selectable and why not.
 *
 * The counterpart to getPackageStartOptions for an hourly-priced package,
 * where the guest picks a start and an end rather than a preset duration. Run
 * as a check PER CANDIDATE END rather than by finding the first clash, so a
 * gap in the middle of the evening greys out only the ends that span it — a
 * 6PM start blocked at 8PM can still offer 7PM.
 *
 * Ends are absolute like start hours (they may exceed 24 on a window that
 * wraps past midnight) and stop at the package's own closing time.
 *
 * @param {object} params - as checkPackageStart, minus `hours`
 * @returns {Array<{ hour: number, hours: number, label: string, available: boolean, reason: string|null, code: string|null }>}
 */
export const getPackageEndOptions = ({ config, pkg, startHour, bookedSlots, currentHour = null }) => {
  const start = Number(startHour);
  if (!Number.isFinite(start)) return [];
  const closing = (Number(pkg?.startHour) || 0) + getPackageWindowHours(pkg);
  const out = [];
  for (let end = start + 1; end <= closing; end += 1) {
    const check = checkPackageStart({ config, pkg, startHour: start, hours: end - start, bookedSlots, currentHour });
    out.push({
      hour: end,
      hours: end - start,
      label: formatPackageHour(end),
      available: check.ok,
      reason: check.ok ? null : check.message,
      code: check.ok ? null : check.code,
    });
  }
  return out;
};

/**
 * The booking payload for a confirmed package selection, in the same
 * `{ [timeSlot]: courts[] }` shape an ordinary booking uses — so it locks,
 * blocks the grid and exports without any special handling.
 *
 * Cleanup hours are included: the club is holding those courts, so they must
 * read as unavailable to everyone. With a zero allowance there are none, and
 * the hour after the event stays sellable.
 *
 * @returns {{ timeSlots: object, eventSlots: string[], bufferSlots: string[] }}
 */
export const buildPackageTimeSlots = (config, pkg, startHour, hours) => {
  const courts = getPackageCourts(pkg);
  const { eventHours, bufferHours } = getPackageOccupiedHours(config, pkg, startHour, hours);
  const timeSlots = {};
  const eventSlots = [];
  const bufferSlots = [];

  eventHours.forEach((hour) => {
    const label = getSlotLabelForHour(config, hour);
    if (!label) return;
    timeSlots[label] = [...courts];
    eventSlots.push(label);
  });
  bufferHours.forEach((hour) => {
    const label = getSlotLabelForHour(config, hour);
    // Skip a cleanup hour the club has no slot for, and never let one
    // overwrite an event hour (a 24h window could wrap onto itself).
    if (!label || timeSlots[label]) return;
    timeSlots[label] = [...courts];
    bufferSlots.push(label);
  });

  return { timeSlots, eventSlots, bufferSlots };
};

/** The weekday a package's pricing should use — the START date's. */
export const getPackageWeekday = (dateStr) => getWeekdayFromDateStr(dateStr);
